<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const store = useStore();

// Props
const props = defineProps({
  conversation: {
    type: Object,
    required: true
  },
  kanbanCard: {
    type: Object,
    required: true
  },
  pipelineId: {
    type: [String, Number],
    required: true
  }
});

// Emits
const emit = defineEmits(['update-conversation']);

// Refs
const showAddProductModal = ref(false);
const showEditProductModal = ref(false);
const editingProduct = ref(null);
const selectedProductId = ref(null);
const quantity = ref(1);
const unitPrice = ref(null);
const currency = ref('BRL');

// Computed
const cardProducts = computed(() => {
  return store.getters['kanbanCardProducts/getCardProducts'](props.kanbanCard.id);
});

const availableProducts = computed(() => {
  return store.getters['products/getProducts'];
});

const uiFlags = computed(() => {
  return store.getters['kanbanCardProducts/getUIFlags'];
});

const totalsByCurrency = computed(() => {
  return store.getters['kanbanCardProducts/getCardTotalsByCurrency'](props.kanbanCard.id);
});

const currencyOptions = computed(() => [
  { value: 'BRL', label: 'Real (R$)', symbol: 'R$' },
  { value: 'USD', label: 'Dólar ($)', symbol: '$' },
  { value: 'EUR', label: 'Euro (€)', symbol: '€' }
]);

const selectedProduct = computed(() => {
  if (!selectedProductId.value) return null;
  return availableProducts.value.find(p => p.id === selectedProductId.value);
});

// Methods
const loadProducts = async () => {
  try {
    await store.dispatch('kanbanCardProducts/get', { 
      cardId: props.kanbanCard.id, 
      pipelineId: props.pipelineId 
    });
  } catch (error) {
    console.error('Erro ao carregar produtos do card:', error);
  }
};

const loadAvailableProducts = async () => {
  try {
    await store.dispatch('products/get');
  } catch (error) {
    console.error('Erro ao carregar produtos disponíveis:', error);
  }
};

const openAddProductModal = () => {
  selectedProductId.value = null;
  quantity.value = 1;
  unitPrice.value = null;
  currency.value = 'BRL';
  showAddProductModal.value = true;
};

const openEditProductModal = (cardProduct) => {
  editingProduct.value = cardProduct;
  selectedProductId.value = cardProduct.product.id;
  quantity.value = cardProduct.quantity;
  unitPrice.value = cardProduct.unit_price;
  currency.value = cardProduct.currency;
  showEditProductModal.value = true;
};

const closeModals = () => {
  showAddProductModal.value = false;
  showEditProductModal.value = false;
  editingProduct.value = null;
  selectedProductId.value = null;
  quantity.value = 1;
  unitPrice.value = null;
  currency.value = 'BRL';
};

const addProduct = async () => {
  if (!selectedProductId.value || quantity.value <= 0) return;

  try {
    const product = selectedProduct.value;
    const productData = {
      product_id: selectedProductId.value,
      quantity: quantity.value,
      unit_price: unitPrice.value || product?.base_price || product?.value,
      currency: currency.value
    };

    // Verificar se o produto já existe no card
    const existingProduct = cardProducts.value.find(cp => cp.product.id === selectedProductId.value);
    
    const response = await store.dispatch('kanbanCardProducts/create', {
      cardId: props.kanbanCard.id,
      pipelineId: props.pipelineId,
      productData
    });

    // Mostrar mensagem apropriada
    if (existingProduct) {
      // Produto já existia - quantidade foi somada
      const addedQuantity = quantity.value;
      const totalQuantity = response.quantity;
      console.log(`Produto "${product.name}" já existia. Adicionadas ${addedQuantity} unidades (total: ${totalQuantity})`);
      // TODO: Mostrar notificação de sucesso para o usuário
    } else {
      // Produto novo criado
      console.log(`Produto "${product.name}" adicionado com sucesso`);
      // TODO: Mostrar notificação de sucesso para o usuário
    }

    closeModals();
    emit('update-conversation', props.conversation);
    
    // Emitir evento para notificar outras páginas sobre a mudança
    window.dispatchEvent(new CustomEvent('productsUpdated', {
      detail: {
        conversationId: props.conversation.id,
        action: 'kanban_product_added',
        productId: product.id,
        cardId: props.kanbanCard.id,
        source: 'kanban'
      }
    }));
  } catch (error) {
    console.error('Erro ao adicionar produto:', error);
    // TODO: Mostrar notificação de erro para o usuário
  }
};

const updateProduct = async () => {
  if (!editingProduct.value || quantity.value <= 0) return;

  try {
    const productData = {
      quantity: quantity.value,
      unit_price: unitPrice.value,
      currency: currency.value
    };

    await store.dispatch('kanbanCardProducts/update', {
      cardId: props.kanbanCard.id,
      pipelineId: props.pipelineId,
      productId: editingProduct.value.id,
      productData
    });

    closeModals();
    emit('update-conversation', props.conversation);
    
    // Emitir evento para notificar outras páginas sobre a mudança
    window.dispatchEvent(new CustomEvent('productsUpdated', {
      detail: {
        conversationId: props.conversation.id,
        action: 'kanban_product_updated',
        productId: editingProduct.value.id,
        cardId: props.kanbanCard.id,
        source: 'kanban'
      }
    }));
  } catch (error) {
    console.error('Erro ao atualizar produto:', error);
  }
};

const removeProduct = async (cardProduct) => {
  if (!confirm('Tem certeza que deseja remover este produto?')) return;

  try {
    await store.dispatch('kanbanCardProducts/delete', {
      cardId: props.kanbanCard.id,
      pipelineId: props.pipelineId,
      productId: cardProduct.id
    });

    emit('update-conversation', props.conversation);
    
    // Emitir evento para notificar outras páginas sobre a mudança
    window.dispatchEvent(new CustomEvent('productsUpdated', {
      detail: {
        conversationId: props.conversation.id,
        action: 'kanban_product_removed',
        productId: cardProduct.id,
        cardId: props.kanbanCard.id
      }
    }));
  } catch (error) {
    console.error('Erro ao remover produto:', error);
  }
};

const formatCurrency = (value, currencyCode) => {
  const symbols = { BRL: 'R$', USD: '$', EUR: '€' };
  const numericValue = parseFloat(value) || 0;
  const symbol = symbols[currencyCode] || currencyCode;
  return `${symbol} ${numericValue.toFixed(2)}`;
};

// Watchers
watch(() => selectedProductId.value, (newProductId) => {
  if (newProductId && selectedProduct.value) {
    unitPrice.value = selectedProduct.value.base_price;
    currency.value = selectedProduct.value.currency || 'BRL';
  }
});

// Lifecycle
onMounted(() => {
  loadProducts();
  loadAvailableProducts();
  
  // Listener para eventos de mudança de funil
  const handleFunnelUpdate = (event) => {
    const { action, currentCardId } = event.detail;
    
    // Se um produto foi adicionado e é o mesmo card, recarregar produtos
    if (action === 'create' && currentCardId === props.kanbanCard.id) {
      setTimeout(() => {
        loadProducts();
      }, 200);
    }
  };
  
  window.addEventListener('funnelUpdated', handleFunnelUpdate);
  
  // Cleanup
  onUnmounted(() => {
    window.removeEventListener('funnelUpdated', handleFunnelUpdate);
  });
});
</script>

<template>
  <div class="space-y-4">
    <!-- Header - Only show add button when products exist -->
    <div v-if="cardProducts.length > 0" class="flex items-center justify-end">
      <button 
        @click="openAddProductModal"
        class="px-3 py-1.5 bg-n-blue-11 text-white rounded-md text-sm font-medium hover:bg-n-blue-12 transition-colors flex items-center gap-2"
      >
        <i class="i-lucide-plus size-4" />
        Adicionar
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="uiFlags.isFetching" class="text-center py-8">
      <div class="animate-spin w-6 h-6 border-2 border-n-blue-11 border-t-transparent rounded-full mx-auto mb-2"></div>
      <p class="text-sm text-n-slate-11">Carregando produtos...</p>
    </div>

    <!-- Products List -->
    <div v-else-if="cardProducts.length > 0" class="space-y-3">
      <div
        v-for="cardProduct in cardProducts"
        :key="cardProduct.id"
        class="bg-n-background border border-n-weak rounded-lg p-4 hover:shadow-md transition-shadow"
      >
        <div class="flex items-start justify-between">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-2">
              <div 
                class="w-3 h-3 rounded-full"
                :style="{ backgroundColor: cardProduct.product.color }"
              ></div>
              <h4 class="font-medium text-n-slate-12 truncate">
                {{ cardProduct.product.name }}
              </h4>
              <span 
                class="px-2 py-0.5 text-xs rounded-full"
                :class="cardProduct.product.type === 'product' 
                  ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                  : 'bg-green-100 text-green-700 border border-green-200'"
              >
                {{ cardProduct.product.type === 'product' ? 'Produto' : 'Serviço' }}
              </span>
            </div>
            
            <p v-if="cardProduct.product.description" class="text-sm text-n-slate-11 mb-2 line-clamp-2">
              {{ cardProduct.product.description }}
            </p>
            
            <div class="flex items-center gap-4 text-sm">
              <span class="text-n-slate-11">
                Qtd: <span class="font-medium text-n-slate-12">{{ cardProduct.quantity }}</span>
              </span>
              <span class="text-n-slate-11">
                Preço: <span class="font-medium text-n-slate-12">{{ cardProduct.formatted_price }}</span>
              </span>
              <span class="text-n-slate-11">
                Total: <span class="font-semibold text-n-blue-11">{{ cardProduct.formatted_total }}</span>
              </span>
            </div>
          </div>
          
          <div class="flex items-center gap-1 ml-3">
            <button
              @click="openEditProductModal(cardProduct)"
              class="w-8 h-8 rounded-md flex items-center justify-center text-n-slate-11 hover:bg-n-slate-2 hover:text-n-slate-12 transition-colors"
              title="Editar produto"
            >
              <i class="i-lucide-edit-2 size-4" />
            </button>
            <button
              @click="removeProduct(cardProduct)"
              class="w-8 h-8 rounded-md flex items-center justify-center text-n-slate-11 hover:bg-red-50 hover:text-red-600 transition-colors"
              title="Remover produto"
            >
              <i class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>
      </div>

      <!-- Totals Summary -->
      <div v-if="Object.keys(totalsByCurrency).length > 0" class="bg-n-slate-1 border border-n-weak rounded-lg p-4">
        <h4 class="font-medium text-n-slate-12 mb-2">Total por Moeda</h4>
        <div class="flex flex-wrap gap-3">
          <div
            v-for="(total, currencyCode) in totalsByCurrency"
            :key="currencyCode"
            class="px-3 py-2 bg-n-background border border-n-weak rounded-md"
          >
            <span class="text-sm font-semibold text-n-blue-11">
              {{ formatCurrency(total, currencyCode) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-8">
      <div class="w-16 h-16 mx-auto mb-4 bg-n-slate-2 rounded-full flex items-center justify-center">
        <i class="i-lucide-shopping-cart size-8 text-n-slate-11" />
      </div>
      <p class="text-n-slate-11 mb-4">Nenhum produto adicionado a este card</p>
      <div class="space-y-2">
        <button 
          v-if="availableProducts.length > 0"
          @click="openAddProductModal"
          class="block mx-auto px-4 py-2 bg-n-blue-11 text-white rounded-md text-sm font-medium hover:bg-n-blue-12 transition-colors"
        >
          Adicionar produto existente
        </button>
        <div v-else class="space-y-2">
          <p class="text-sm text-n-slate-9">Nenhum produto cadastrado no sistema</p>
          <a 
            :href="`/app/accounts/${$store.getters.getCurrentAccountId}/settings/products/list`"
            target="_blank"
            class="inline-flex items-center gap-2 px-4 py-2 border border-n-weak text-n-slate-11 rounded-md text-sm font-medium hover:bg-n-slate-1 transition-colors"
          >
            <i class="i-lucide-plus size-4" />
            Criar primeiro produto
          </a>
        </div>
      </div>
    </div>

    <!-- Add Product Modal -->
    <div v-if="showAddProductModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-n-background border border-n-weak rounded-lg p-6 w-full max-w-lg mx-4">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-base font-semibold text-n-slate-12">Adicionar Produto ao Card</h3>
          <button @click="closeModals" class="text-n-slate-11 hover:text-n-slate-12">
            <i class="i-lucide-x size-5" />
          </button>
        </div>

        <div class="space-y-4">
          <!-- Product Selection with Preview -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">Selecionar Produto</label>
            <select
              v-model="selectedProductId"
              class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-background text-n-slate-12 focus:ring-2 focus:ring-n-blue-11 focus:border-transparent"
            >
              <option value="">Escolha um produto da lista</option>
              <option
                v-for="product in availableProducts"
                :key="product.id"
                :value="product.id"
              >
                {{ product.name }} - {{ product.formatted_price || `${product.currency_symbol || 'R$'} ${product.base_price || product.value}` }}
              </option>
            </select>
            
            <!-- Product Preview -->
            <div v-if="selectedProduct" class="mt-3 p-3 bg-n-slate-1 border border-n-weak rounded-md">
              <div class="flex items-center gap-3">
                <div 
                  class="w-8 h-8 rounded-lg flex items-center justify-center text-white"
                  :style="{ backgroundColor: selectedProduct.color }"
                >
                  <i class="i-lucide-package size-4" />
                </div>
                <div class="flex-1">
                  <h4 class="font-medium text-n-slate-12">{{ selectedProduct.name }}</h4>
                  <p v-if="selectedProduct.description" class="text-sm text-n-slate-11 line-clamp-2">
                    {{ selectedProduct.description }}
                  </p>
                  <div class="flex items-center gap-2 mt-1">
                    <span 
                      class="px-2 py-0.5 text-xs rounded-full"
                      :class="selectedProduct.type === 'product' 
                        ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                        : 'bg-green-100 text-green-700 border border-green-200'"
                    >
                      {{ selectedProduct.type === 'product' ? 'Produto' : 'Serviço' }}
                    </span>
                    <span class="text-sm text-n-slate-11">
                      Preço base: {{ selectedProduct.formatted_price || `${selectedProduct.currency_symbol || 'R$'} ${selectedProduct.base_price || selectedProduct.value}` }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <!-- Quantity -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">Quantidade</label>
              <input
                v-model.number="quantity"
                type="number"
                min="1"
                class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-background text-n-slate-12 focus:ring-2 focus:ring-n-blue-11 focus:border-transparent"
              />
            </div>

            <!-- Currency -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">Moeda</label>
              <select
                v-model="currency"
                class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-background text-n-slate-12 focus:ring-2 focus:ring-n-blue-11 focus:border-transparent"
              >
                <option
                  v-for="option in currencyOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>

          <!-- Unit Price -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              Preço Unitário 
              <span class="text-xs text-n-slate-11">(deixe vazio para usar o preço base do produto)</span>
            </label>
            <input
              v-model.number="unitPrice"
              type="number"
              step="0.01"
              min="0"
              :placeholder="selectedProduct ? `${selectedProduct.base_price || selectedProduct.value}` : '0.00'"
              class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-background text-n-slate-12 focus:ring-2 focus:ring-n-blue-11 focus:border-transparent"
            />
          </div>

          <!-- Total Preview -->
          <div v-if="selectedProduct && quantity > 0" class="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-blue-800">Total:</span>
              <span class="text-lg font-semibold text-blue-900">
                {{ formatCurrency((unitPrice || selectedProduct.base_price || selectedProduct.value) * quantity, currency) }}
              </span>
            </div>
          </div>
        </div>

        <div class="flex gap-3 mt-6">
          <button
            @click="closeModals"
            class="flex-1 px-4 py-2 border border-n-weak text-n-slate-11 rounded-md hover:bg-n-slate-1 transition-colors"
          >
            Cancelar
          </button>
          <button
            @click="addProduct"
            :disabled="!selectedProductId || quantity <= 0 || uiFlags.isCreating"
            class="flex-1 px-4 py-2 bg-n-blue-11 text-white rounded-md hover:bg-n-blue-12 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="uiFlags.isCreating">Adicionando...</span>
            <span v-else>Adicionar ao Card</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Product Modal -->
    <div v-if="showEditProductModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-n-background border border-n-weak rounded-lg p-6 w-full max-w-md mx-4">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-base font-semibold text-n-slate-12">Editar Produto</h3>
          <button @click="closeModals" class="text-n-slate-11 hover:text-n-slate-12">
            <i class="i-lucide-x size-5" />
          </button>
        </div>

        <div class="space-y-4">
          <!-- Product Info (Read-only) -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">Produto</label>
            <div class="px-3 py-2 border border-n-weak rounded-md bg-n-slate-1 text-n-slate-11">
              {{ editingProduct?.product?.name }}
            </div>
          </div>

          <!-- Quantity -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">Quantidade</label>
            <input
              v-model.number="quantity"
              type="number"
              min="1"
              class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-background text-n-slate-12 focus:ring-2 focus:ring-n-blue-11 focus:border-transparent"
            />
          </div>

          <!-- Unit Price -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">Preço Unitário</label>
            <input
              v-model.number="unitPrice"
              type="number"
              step="0.01"
              min="0"
              class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-background text-n-slate-12 focus:ring-2 focus:ring-n-blue-11 focus:border-transparent"
            />
          </div>

          <!-- Currency -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">Moeda</label>
            <select
              v-model="currency"
              class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-background text-n-slate-12 focus:ring-2 focus:ring-n-blue-11 focus:border-transparent"
            >
              <option
                v-for="option in currencyOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>

        <div class="flex gap-3 mt-6">
          <button
            @click="closeModals"
            class="flex-1 px-4 py-2 border border-n-weak text-n-slate-11 rounded-md hover:bg-n-slate-1 transition-colors"
          >
            Cancelar
          </button>
          <button
            @click="updateProduct"
            :disabled="quantity <= 0 || uiFlags.isUpdating"
            class="flex-1 px-4 py-2 bg-n-blue-11 text-white rounded-md hover:bg-n-blue-12 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="uiFlags.isUpdating">Salvando...</span>
            <span v-else>Salvar</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>